import { Clock3, Loader2, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AiData } from '../ui/ai-data'
import { AiProgress } from '../ui/ai-motion'
import type { AgentRun } from './agent-pass-state'
import type { ReviewedRow } from './types'

// How the quote AI agent's pass shows in the import review (USA-1340): its progress over the
// review and each row's state while it works.

function retryTime(value: string | null | undefined) {
  if (!value) return null
  return new Date(value).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

function progress(rows: ReviewedRow[]) {
  const assigned = rows.filter((row) => row.agent)
  return { total: assigned.length, finished: assigned.filter((row) => row.agent?.status !== 'working').length }
}

/** The pass's progress, inline in the review's status line; once it ends the same spot says what it
 * checked, so nothing moves. */
export function AgentPassNote({ run, rows }: { run: AgentRun | null | undefined; rows: ReviewedRow[] }) {
  if (!run) return null
  const { total, finished } = progress(rows)
  const retryAt = run.status === 'retrying' ? retryTime(run.retry_at) : null
  return (
    <>
      {' · '}
      <span
        className={cn(
          'inline-flex items-center gap-1 align-bottom',
          run.status === 'done' ? 'text-muted-foreground' : 'text-violet-800 dark:text-violet-300',
        )}
      >
        {run.status === 'done' ? (
          <Sparkles aria-hidden="true" className="size-3.5 shrink-0" />
        ) : run.status === 'retrying' ? (
          <Clock3 aria-hidden="true" className="size-3.5 shrink-0 text-violet-600" />
        ) : (
          <Loader2 aria-hidden="true" className="size-3.5 shrink-0 animate-spin text-violet-600 motion-reduce:animate-none" />
        )}
        <span className="tabular-nums">
          {run.status === 'done'
            ? `SmartQuote checked ${total}`
            : run.status === 'retrying'
              ? `SmartQuote paused${retryAt ? ` until ${retryAt}` : ''}`
              : `SmartQuote finishing ${finished} of ${total}`}
        </span>
      </span>
    </>
  )
}

/** The pass's progress along the bottom edge of the review header. */
export function AgentPassProgress({ run, rows }: { run: AgentRun | null | undefined; rows: ReviewedRow[] }) {
  if (run?.status !== 'working') return null
  const { total, finished } = progress(rows)
  return <AiProgress className="absolute inset-x-0 bottom-0" value={total ? finished / total : 0} />
}

/** A row the agent is still working on, or could not finish, beside the row's match heading. */
export function AgentRowStatus({ row }: { row: ReviewedRow }) {
  if (row.agent?.status === 'working') {
    return (
      <span className="inline-flex items-center gap-1 font-normal text-violet-800 dark:text-violet-300">
        <Loader2 aria-hidden="true" className="size-3.5 animate-spin text-violet-600 motion-reduce:animate-none" />
        SmartQuote checking
      </span>
    )
  }
  if (row.agent?.status === 'unfinished') {
    return <span className="font-normal">SmartQuote couldn't finish this item</span>
  }
  return null
}

/** The agent found no catalog product that fits: the row is quoted as a custom item. */
export function AgentCustomItem({ reason }: { reason: string }) {
  return (
    <AiData className="max-w-xl px-3 py-2" label="AI recommendation">
      <p className="text-sm font-medium">Custom item</p>
      <p className="mt-0.5 text-xs text-muted-foreground">{reason}</p>
    </AiData>
  )
}
